import React, { useState, useEffect } from 'react';
import { getAddressLink, getTransactionLink, hasExplorerSupport } from '../utils/BlockchainExplorer';
import AddressDisplay from './AddressDisplay';

/**
 * LinkDisplay - Displays addresses or transactions as hyperlinks when explorer is available
 * Falls back to regular display when no explorer is available (dev/devcli networks)
 */
const LinkDisplay = ({ 
  value, 
  type = 'address', // 'address' or 'transaction'
  provider,
  className = '',
  style = {},
  prefix = '',
  suffix = '',
  showFullOnHover = true,
  copyable = true
}) => {
  const [explorerLink, setExplorerLink] = useState(null);
  const [hasExplorer, setHasExplorer] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const updateExplorerLink = async () => {
      if (!provider || !value) {
        setLoading(false);
        return;
      }

      try {
        const explorerSupported = await hasExplorerSupport(provider);
        setHasExplorer(explorerSupported);

        if (explorerSupported) {
          let link = null;
          if (type === 'address') {
            link = await getAddressLink(provider, value);
          } else if (type === 'transaction') {
            link = await getTransactionLink(provider, value);
          }
          setExplorerLink(link);
        }
      } catch (error) {
        console.error('Failed to generate explorer link:', error);
      } finally {
        setLoading(false);
      }
    };

    updateExplorerLink();
  }, [provider, value, type]);

  if (loading) {
    return <span className={`text-gray-400 ${className}`}>Loading...</span>;
  }

  // For addresses, use the existing AddressDisplay component when no explorer is available
  if (type === 'address' && !hasExplorer) {
    return (
      <AddressDisplay
        address={value}
        showFullOnHover={showFullOnHover}
        copyable={copyable}
        className={className}
        style={style}
        prefix={prefix}
        suffix={suffix}
      />
    );
  }

  // For transactions or when no explorer is available, show plain text
  if (!hasExplorer || !explorerLink) {
    const displayValue = type === 'address' ? 
      (value ? `${value.substring(0, 6)}...${value.substring(value.length - 4)}` : 'N/A') :
      value;

    return (
      <span 
        className={`font-mono text-xs ${copyable ? 'cursor-pointer hover:bg-gray-100 rounded px-1' : ''} ${className}`}
        style={style}
        onClick={copyable ? () => handleCopy(value) : undefined}
        title={copyable ? `Click to copy ${type}: ${value}` : `${type}: ${value}`}
      >
        {prefix}{displayValue}{suffix}
      </span>
    );
  }

  // Show as hyperlink when explorer is available
  const displayValue = type === 'address' ? 
    (value ? `${value.substring(0, 6)}...${value.substring(value.length - 4)}` : 'N/A') :
    (value ? `${value.substring(0, 10)}...${value.substring(value.length - 8)}` : 'N/A');

  return (
    <span className={className} style={style}>
      {prefix}
      <a
        href={explorerLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline font-mono text-xs"
        title={`View ${type} on blockchain explorer: ${value}`}
      >
        {displayValue}
      </a>
      {suffix}
    </span>
  );
};

/**
 * Helper function to copy text to clipboard
 */
const handleCopy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    console.log(`Copied ${text} to clipboard`);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
  }
};

/**
 * AddressLink - Convenience component for displaying address links
 */
export const AddressLink = (props) => (
  <LinkDisplay {...props} type="address" />
);

/**
 * TransactionLink - Convenience component for displaying transaction links
 */
export const TransactionLink = (props) => (
  <LinkDisplay {...props} type="transaction" />
);

export default LinkDisplay;